import type { JsonType } from 'posthog-js'

/**
 * Parses NEXT_PUBLIC_LOCAL_FEATURE_FLAGS env var into a PostHog bootstrap-compatible object.
 *
 * Usage: set in your app's .env.local file:
 *   NEXT_PUBLIC_LOCAL_FEATURE_FLAGS=ff-accounts-payable-enabled,ff-new-navbar-enabled
 *
 * This allows `useFeatureFlagEnabled` from posthog-js/react to work locally
 * without connecting to PostHog.
 */
export const getLocalFeatureFlags = (): Record<string, boolean> => {
  const raw =
    // eslint-disable-next-line alternative/no-invalid-env-usage
    process.env.NEXT_PUBLIC_LOCAL_FEATURE_FLAGS ?? ''

  if (!raw.trim()) return {}

  return raw
    .split(',')
    .map(flag => flag.trim())
    .filter(Boolean)
    .reduce<Record<string, boolean>>((acc, flag) => {
      acc[flag] = true
      return acc
    }, {})
}

/**
 * Parses NEXT_PUBLIC_LOCAL_FEATURE_FLAG_PAYLOADS into a PostHog
 * bootstrap-compatible payload map.
 *
 * Usage: set in your app's .env.local file, as JSON:
 *   NEXT_PUBLIC_LOCAL_FEATURE_FLAG_PAYLOADS={"ff-partner-assistant-enabled":{"outage_outbox_enabled":true}}
 *
 * The companion to `getLocalFeatureFlags`, which can only bootstrap BOOLEANS.
 * A growing number of features gate on a flag's PAYLOAD rather than on the flag
 * itself — `useFFPayload(...)` then `payload?.some_key === true`. Locally
 * PostHog is initialised with a placeholder key against
 * `api_host: 'https://localhost'` with capturing opted out, so no live flag
 * fetch ever fills a payload in. Without this, every payload-gated feature is
 * unreachable in local dev and silently renders its pre-flag fallback instead
 * — found while trying to reach the Alti-branded "New reminder" button, which
 * gated on a `ff-partner-assistant-enabled` payload key at the time (it now
 * has its own flag, `ff-emails-by-alti-enabled`).
 *
 * Invalid or non-object JSON yields `{}` rather than throwing: a typo in one
 * developer's env file must not take the app down at init. The warning keeps
 * the typo visible instead of failing silently.
 */
export const getLocalFeatureFlagPayloads = (): Record<string, JsonType> => {
  const raw =
    // eslint-disable-next-line alternative/no-invalid-env-usage
    process.env.NEXT_PUBLIC_LOCAL_FEATURE_FLAG_PAYLOADS ?? ''

  if (!raw.trim()) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        'NEXT_PUBLIC_LOCAL_FEATURE_FLAG_PAYLOADS must be a JSON object mapping flag key to payload; ignoring'
      )
      return {}
    }
    return parsed as Record<string, JsonType>
  } catch {
    // eslint-disable-next-line no-console
    console.warn(
      'NEXT_PUBLIC_LOCAL_FEATURE_FLAG_PAYLOADS is not valid JSON; ignoring'
    )
    return {}
  }
}
